import { gql } from 'graphql-tag'
import { timePeriodFragment } from '../scoreCard/ScoreCardQueries'
import { hesburgerEfficiencyBoundaryFragment } from '../station/stationQueries'

const generalFragment = gql`
  fragment WeeklyInfoGeneralTypeFragment on WeeklyInfoGeneralType {
    id
    profitCenterType
    totalBudgetedSales
    totalBudgetedHours
    totalBudgetedSalesTaxfree
    totalPlannedHours
    totalPlannedSales
    plannedEfficiency
    budgetedEfficiency
    budgetedEfficiencyTaxfree
    totalActualSales
    totalActualHours
    totalActualSalesTaxfree
    isActionRequired
  }
`

const restaurantFragment = gql`
  fragment WeeklyInfoRestaurantTypeFragment on WeeklyInfoRestaurantType {
    id
    profitCenterType
    salesIncentiveTarget
    totalActualSalesIncentive
    isActionRequired
    currentWeekSalesIncentiveTarget
    currentWeekTotalActualSalesIncentive
  }
`

const kitchenFragment = gql`
  fragment WeeklyInfoKitchenTypeFragment on WeeklyInfoKitchenType {
    id
    profitCenterType
    productGroupName
    productGroupId
    totalWastageAmount
    totalWastageVolume
    totalSalesAmount
    isActionRequired
    totalSalesVolume
  }
`

const buffetWastageFragment = gql`
  fragment WeeklyInfoBuffetWastageFragment on WeeklyInfoBuffetWastageType {
    id
    buffetWastages {
      date
      isAvailable
    }
    isActionRequired
  }
`

const customerPathFragment = gql`
  fragment WeeklyInfoCustomerPathFragment on WeeklyInfoCustomerPathType {
    id
    description
    isActionRequired
  }
`

const marketFragment = gql`
  fragment WeeklyInfoMarketTypeFragment on WeeklyInfoMarketType {
    id
    description
    isActionRequired
  }
`

const scoreCardFragment = gql`
  fragment WeeklyInfoScoreCardFragment on WeeklyInfoScoreCardType {
    id
    isActionRequired
  }
`

const hesburgerEfficiencyFactorFragment = gql`
  fragment HesburgerEfficiencyFactorFragment on HesburgerEfficiencyFactor {
    date
    ipShare
    avgPurchase
    salesMargin
    productWastage
    raWastage
    inventoryDifferences
    intakes
    readinessCheeseBurger
    readinessDoubleBurger
    readinessCampaign
    isActionRequired
  }
`

export const fetchWeeklyInfo = gql`
  query FetchWeeklyInfo($stationId: ID!, $date: String!, $type: WeeklyInfoEnumType!) {
    weeklyInfo(stationId: $stationId, date: $date, type: $type) {
      ...WeeklyInfoGeneralTypeFragment
      ...WeeklyInfoRestaurantTypeFragment
      ...WeeklyInfoKitchenTypeFragment
      ...WeeklyInfoMarketTypeFragment
      ...WeeklyInfoCustomerPathFragment
      ...WeeklyInfoBuffetWastageFragment
    }
    wastageBoundaries: getAllWastageBoundariesForAllProductTypes(stationId: $stationId) {
      productType
      lowerWastageBounds
      upperWastageBounds
    }
  }

  ${generalFragment}
  ${restaurantFragment}
  ${customerPathFragment}
  ${kitchenFragment}
  ${buffetWastageFragment}
  ${marketFragment}
`

export const fetchWeeklyInfoMarketSalesAndProfit = gql`
  query FetchWeeklyInfoMarketSalesAndProfit($stationId: ID!, $date: String!) {
    weeklyInfoMarketSalesAndProfit(stationId: $stationId, date: $date) {
      id
      salesAmountTaxfree
      salesVolume
      salesAmountTaxfreeLastYear
      salesVolumeLastYear
      cdtId
      cdtName
      isActionRequired
      childVolumesAndSums {
        id
        cdtId
        cdtName
        salesVolume
        salesAmountTaxfree
        salesAmountTaxfreeLastYear
        salesVolumeLastYear
        isActionRequired
      }
    }

    chainMarketSettings {
      id
      name
      startDate
      endDate
      value
    }
  }
`

export const fetchWeeklyInfoMarketWastages = gql`
  query FetchWeeklyInfoMarketWastages($stationId: ID!, $date: String!) {
    weeklyInfoMarketWastages(stationId: $stationId, date: $date) {
      id
      wastageAmountTaxfree
      wastageAmountTaxfreePreviousWeek
      wastagePercentage
      wastagePercentagePreviousWeek
      salesAmountTaxfree
      cdtId
      cdtName
      isActionRequired
      wastageUpperBound
      wastageLowerBound
      children {
        id
        cdtId
        cdtName
        wastageAmountTaxfree
        wastageAmountTaxfreePreviousWeek
        wastagePercentage
        wastagePercentagePreviousWeek
        salesAmountTaxfree
        isActionRequired
        wastageUpperBound
        wastageLowerBound
        children {
          id
          cdtId
          cdtName
          wastageAmountTaxfree
          wastageAmountTaxfreePreviousWeek
          wastagePercentage
          wastagePercentagePreviousWeek
          salesAmountTaxfree
          isActionRequired
          wastageUpperBound
          wastageLowerBound
        }
      }
    }
  }
`

export const fetchWeeklyInfoScoreCard = gql`
  query FetchWeeklyInfoScoreCard($stationId: ID!, $date: String!, $type: WeeklyInfoEnumType!, $row: String!) {
    weeklyInfo: weeklyInfoScoreCard(stationId: $stationId, date: $date, type: $type, row: $row) {
      ...WeeklyInfoScoreCardFragment
    }
  }

  ${scoreCardFragment}
`

export const upsertWeeklyInfo = gql`
  mutation UpsertWeeklyInfo($stationId: ID!, $date: String!, $weeklyInfo: UpdateWeeklyInfoInput!) {
    upsertWeeklyInfo(stationId: $stationId, date: $date, weeklyInfo: $weeklyInfo)
  }
`

export const fetchWeeklyInfoNotes = gql`
  query FetchWeeklyInfoNotes($stationId: ID!, $date: String!, $type: WeeklyInfoEnumType!) {
    weeklyInfoNotes(stationId: $stationId, date: $date, type: $type) {
      id
      body
    }
  }
`

export const createNote = gql`
  mutation CreateNewNote($stationId: ID!, $date: String!, $type: WeeklyInfoEnumType!, $body: String!) {
    createWeeklyInfoNotes(stationId: $stationId, date: $date, type: $type, body: $body) {
      id
      body
    }
  }
`
export const updateNote = gql`
  mutation UpdateNote($noteId: ID!, $updatedBody: String) {
    updateWeeklyInfoNote(noteId: $noteId, updatedBody: $updatedBody) {
      id
      body
    }
  }
`
export const deleteNote = gql`
  mutation DeleteNote($noteId: ID!) {
    deleteWeeklyInfoNotes(noteId: $noteId)
  }
`

export const fetchHesburgerEfficiencies = gql`
  query FetchHesburgerEfficiencies($stationId: ID!, $date: String!) {
    hesburgerEfficiencyFactor(stationId: $stationId, date: $date) {
      ...HesburgerEfficiencyFactorFragment
    }
    hesburgerEfficiencyBoundary(stationId: $stationId) {
      ...HesburgerEfficiencyBoundaryFragment
    }
  }
  ${hesburgerEfficiencyFactorFragment}
  ${hesburgerEfficiencyBoundaryFragment}
`

export const uploadHesburgerEfficiencies = gql`
  mutation UploadHesburgerEfficiencies($file: Upload!, $date: String!) {
    uploadHesburgerEfficiencies(file: $file, date: $date)
  }
`

export const saveHesburgerEfficiencyFactor = gql`
  mutation SaveHesburgerEfficiencyFactor(
    $stationId: ID!
    $date: String!
    $data: UpsertHesburgerEfficiencyFactorInput!
  ) {
    upsertHesburgerEfficiencyFactor(stationId: $stationId, date: $date, data: $data) {
      ...HesburgerEfficiencyFactorFragment
    }
  }

  ${hesburgerEfficiencyFactorFragment}
`

export const fetchRestaurantPageScoreCardStats = gql`
  query FetchStationScoreCardStats($stationId: ID!, $date: String) {
    scoreCardStats: newScoreCardStationStats(stationId: $stationId, date: $date) {
      toiletChecks {
        ...ScoreCardTimePeriodFragment
      }
      dailyChecklistUsage {
        ...ScoreCardTimePeriodFragment
      }
      continuingChecklistUsageMonthly {
        ...ScoreCardTimePeriodFragment
      }
      continuingChecklistUsageWeekly {
        ...ScoreCardTimePeriodFragment
      }
      continuingChecklistUsageDaily {
        ...ScoreCardTimePeriodFragment
      }
      hourlyHallChecklistUsage {
        ...ScoreCardTimePeriodFragment
      }
      hourlyBuffetChecklistUsage {
        ...ScoreCardTimePeriodFragment
      }
      hourlyVitrineChecklistUsage {
        ...ScoreCardTimePeriodFragment
      }
    }
  }
  ${timePeriodFragment}
`

export const fetchKitchenPageScoreCardStats = gql`
  query FetchStationScoreCardStats($stationId: ID!, $date: String) {
    scoreCardStats: newScoreCardStationStats(stationId: $stationId, date: $date) {
      dailyInfoUsage {
        ...ScoreCardTimePeriodFragment
      }
    }
  }
  ${timePeriodFragment}
`

export const fetchManagerPageScoreCardStats = gql`
  query FetchStationScoreCardStats($stationId: ID!) {
    scoreCardStats: newScoreCardStationStats(stationId: $stationId) {
      budgetEfficiency {
        ...ScoreCardTimePeriodFragment
      }
      targetEfficiency {
        ...ScoreCardTimePeriodFragment
      }
    }
  }
  ${timePeriodFragment}
`
